import CancelIcon from '@mui/icons-material/Cancel';
import ChatIcon from '@mui/icons-material/Chat';
import EditSquareIcon from '@mui/icons-material/EditSquare';
import LogoutIcon from '@mui/icons-material/Logout';
import Badge from "@mui/material/Badge";
import Portal from "@mui/material/Portal";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { ConfirmResponse, useConfirmDialog } from 'app/providers/confirm-dialog';
import { useDialog } from 'app/providers/dialog';
import { useAttorneyHubDispatch } from 'app/realtime-store/redux-store';
import LoadingButton from 'common/components/loading-button';
import Guid from "common/values/guid/guid";
import { getForumsByTopic, populateForumsByTopic } from 'messaging/entities/forum/store/forums-redux-slice';
import Topic from 'messaging/values/topic';
import { enqueueSnackbar } from 'notistack';
import React, { useEffect } from "react";
import { NavigateOptions, To } from 'react-router';
import { useSession } from 'users/session/session-context';
import { addProposal, addProposalBuilder } from 'work/entities/proposal/store/proposals-redux-slice';
import ProposalBuilder from 'work/entities/proposal/utils/proposal-builder';
import {
  addWorkAgreement,
  getWorkAgreementById,
  removeWorkAgreement
} from "work/entities/work-agreement/store/work-agreement-redux-slice";
import ChatDrawer from 'work/entities/work-agreement/view/chat-drawer';
import ConflictsTab from "work/entities/work-agreement/view/tabs/conflicts-tab";
import DetailsTab from "work/entities/work-agreement/view/tabs/details-tab";
import DiscountTab from "work/entities/work-agreement/view/tabs/discount-tab";
import FeeScheduleTab from "work/entities/work-agreement/view/tabs/fee-schedule-tab";
import InvoicesTab from 'work/entities/work-agreement/view/tabs/invoices-tab';
import PoliciesTab from "work/entities/work-agreement/view/tabs/policies-tab";
import TeamTab from "work/entities/work-agreement/view/tabs/team-tab";
import { Masked, WorkAgreementFieldCategory } from "work/entities/work-agreement/work-agreement";
import {
  ActionsContainer,
  ButtonContainer,
  Content,
  DialogTab,
  Header,
  MessageButton,
  MessageButtonLabel,
  MessageButtons,
  SecondaryActions,
  TabContent,
  TabsContainer
} from "work/entities/views/work-dialog-shared-components";

const WorkAgreementActions = styled("section")(
  ({ theme }) => ({
    [theme.breakpoints.down("md")]: {
      display: "flex",
      flexDirection: "column",
      flexGrow: 1,
      flexWrap: "nowrap",
    },
    alignContent: "end",
    alignItems: "center",
    display: "grid",
    flex: 1,
    gridTemplateColumns: "repeat(auto-fit, minmax(15rem, 1fr))",
    gap: theme.spacing(1),
    minHeight: "64px",
  }));
const WorkAgreementActionSpan = styled("span")(
  ({ theme }) => ({
    minWidth: theme.spacing(24),
    width: "100%"
  }));
const WorkAgreementActionButton = styled(LoadingButton)(
  () => ({
    width: "100%",
    whiteSpace: "nowrap",
    "&.Mui-disabled": {
      color: "rgba(0, 0, 0, 0.26) !important",
      backgroundColor: "rgba(0, 0, 0, 0.12) !important",
    },
  }));

type WorkAgreementDialogProps = {
  workAgreementId?: Guid;
  readOnly?: boolean;
  navigate: (to: To, options?: NavigateOptions) => void | Promise<void>
};

export default function WorkAgreementDialog(
  props: Readonly<WorkAgreementDialogProps>
) {
  const {
    workAgreementId,
    readOnly,
    navigate
  } = props;

  const [activeTab, setActiveTab] = React.useState<WorkAgreementFieldCategory>(
    WorkAgreementFieldCategory.Details
  );
  const [isRevising, setIsRevising] = React.useState(false);
  const [isEnding, setIsEnding] = React.useState(false);
  const [isLeaving, setIsLeaving] = React.useState(false);
  const [isChatPanelOpen, setIsChatPanelOpen] = React.useState(false);
  const workAgreement = getWorkAgreementById(workAgreementId);
  const topic = new Topic("Work.Agreement", workAgreementId);
  const forums = getForumsByTopic(topic);

  const session = useSession();
  const dispatch = useAttorneyHubDispatch();
  const confirm = useConfirmDialog();
  const { closeDialog } = useDialog();

  useEffect(() => {
    dispatch(populateForumsByTopic({ session, topic }));
  },
    [
      workAgreementId
    ]
  );

  function handleTabChange(
    _event: React.SyntheticEvent | null,
    newTab: WorkAgreementFieldCategory
  ) {
    setActiveTab(newTab);
  }

  function adjustDialogPosition(panelOpen: boolean) {
    for (const dialog of document.getElementsByClassName("MuiDialog-root")) {
      dialog.setAttribute(
        "style",
        `padding-right: ${panelOpen ? "0px" : "400px"
        }; transition: padding-right 225ms;`
      );
    }
  }

  function handleCloseChatPanel() {
    setIsChatPanelOpen(false);
    adjustDialogPosition(true);
  }

  function handleOpenChatPanel() {
    setIsChatPanelOpen(true);
    adjustDialogPosition(false);
  }

  async function handleEndWorkAgreement() {
    if (!workAgreement?.id) {
      console.error("Work agreement not found");
      return;
    }

    const response = await confirm({
      title: "End Work Agreement?",
      message: "This will void the work agreement for any future work and only work performed " +
        "before this date will be billable until a new proposal is agreed to.",
      okButtonText: "End"
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      setIsEnding(true);
      const updatedWorkAgreement = await workAgreement.endAgreement(session.user?.id);
      dispatch(addWorkAgreement(updatedWorkAgreement));
      closeDialog();
      navigate(`/work/archived/view/${updatedWorkAgreement.id?.value}`);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        "Failed to end work agreement",
        { variant: "error" }
      );
    } finally {
      setIsEnding(false);
    }
  }

  async function handleReviseWorkAgreement() {
    if (!workAgreement?.id) {
      throw new Error("Work agreement not found");
    }
    if (!session.user) {
      throw new Error("User not found");
    }

    const response = await confirm({
      title: "Revise Work Agreement?",
      message: "This will void the work agreement for any future work and only work performed " +
        "before this date will be billable until a new proposal is agreed to.",
      okButtonText: "Revise"
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      setIsRevising(true);

      const { proposal } = await workAgreement.reviseAgreement(session);

      if (!proposal.id) {
        console.error(new Error("Proposal ID not returned"));
        return;
      }

      const proposalBuilder = ProposalBuilder.fromWorkAgreement(
        session.user,
        workAgreement
      );
      proposal.createRedline();

      dispatch(addProposalBuilder({
        proposalId: proposal.id,
        builder: proposalBuilder
      }));
      dispatch(addProposal(proposal));

      navigate(`/proposals/drafts/revise/${proposal.id?.value}`);
      closeDialog();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        "Failed to revise work agreement",
        { variant: "error" }
      );
    } finally {
      setIsRevising(false);
    }
  }

  async function handleLeaveTeam() {
    if (!workAgreement?.id) {
      console.error("Work agreement not found");
      return;
    }
    if (!session.user?.id) {
      console.error("User not found");
      return;
    }

    const response = await confirm({
      title: "Leave Team?",
      message: "Are you sure you want to leave the vendor-team?",
      okButtonText: "Leave"
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      setIsLeaving(true);
      await workAgreement.quitWorkTeam(session.user.id, session);

      enqueueSnackbar(
        "You have left the vendor-team",
        { variant: "success" }
      );
      dispatch(removeWorkAgreement(workAgreement.id));
      closeDialog();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        "Failed to leave vendor-team",
        { variant: "error" }
      );
    } finally {
      setIsLeaving(false);
    }
  }

  return (
    <>
      <Header>
        <TabsContainer
          variant="scrollable"
          scrollButtons="auto"
          indicatorColor="primary"
          textColor="primary"
          value={activeTab}
          onChange={handleTabChange}
        >
          <DialogTab
            value={WorkAgreementFieldCategory.Details}
            label="Details"
          />
          <DialogTab
            value={WorkAgreementFieldCategory.Team}
            label="Team"
          />
          <DialogTab
            value={WorkAgreementFieldCategory.FeeSchedule}
            label="Fee Schedule"
          />
          <DialogTab
            value={WorkAgreementFieldCategory.Conflicts}
            label="Conflicts"
          />
          <DialogTab
            value={WorkAgreementFieldCategory.Policies}
            label="Policies"
          />
          <DialogTab
            value={WorkAgreementFieldCategory.Discount}
            label="Discount"
          />
          <DialogTab
            value={WorkAgreementFieldCategory.Invoices}
            label="Invoices"
          />
        </TabsContainer>
      </Header>
      <Content>
        <TabContent>
          {workAgreement && (
            <>
              <DetailsTab
                workAgreement={workAgreement}
                activeTab={activeTab}
              />
              <TeamTab
                workAgreement={workAgreement}
                activeTab={activeTab}
              />
              <FeeScheduleTab
                workAgreement={workAgreement}
                activeTab={activeTab}
              />
              <ConflictsTab
                workAgreement={workAgreement}
                activeTab={activeTab}
              />
              <PoliciesTab
                workAgreement={workAgreement}
                activeTab={activeTab}
              />
              <DiscountTab
                workAgreement={workAgreement}
                activeTab={activeTab}
              />
              <InvoicesTab
                workAgreement={workAgreement}
                activeTab={activeTab}
              />
            </>
          )}
        </TabContent>
      </Content>
      <ActionsContainer>
        <SecondaryActions />
        <WorkAgreementActions>
          {(
            workAgreement?.client?.userId.isEqualTo(session.user?.id) ||
            (Masked.isNotMasked(workAgreement?.team) &&
              workAgreement?.team?.leader?.userId.isEqualTo(session.user?.id))
          ) && (
              <Tooltip title="Create New Proposal" placement="top">
                <WorkAgreementActionSpan>
                  <WorkAgreementActionButton
                    variant="contained"
                    color="primary"
                    startIcon={<EditSquareIcon />}
                    loading={isRevising}
                    disabled={readOnly || isEnding || isRevising || isLeaving}
                    onClick={handleReviseWorkAgreement}
                  >
                    Revise
                  </WorkAgreementActionButton>
                </WorkAgreementActionSpan>
              </Tooltip>
            )}
          {workAgreement?.client?.userId.isEqualTo(session.user?.id) && (
            <Tooltip title="End Work Agreement" placement="top">
              <WorkAgreementActionSpan>
                <WorkAgreementActionButton
                  variant="contained"
                  color="error"
                  startIcon={<CancelIcon />}
                  loading={isEnding}
                  disabled={readOnly || isEnding || isRevising || isLeaving}
                  onClick={handleEndWorkAgreement}
                >
                  End Agreement
                </WorkAgreementActionButton>
              </WorkAgreementActionSpan>
            </Tooltip>
          )}
          {(
            Masked.isNotMasked(workAgreement?.team) &&
            workAgreement?.team?.members.some((member) =>
              member.userId.isEqualTo(session.user?.id))
          ) && (
              <WorkAgreementActionButton
                variant="contained"
                color="primary"
                startIcon={<LogoutIcon />}
                loading={isLeaving}
                disabled={readOnly || isEnding || isRevising || isLeaving}
                onClick={handleLeaveTeam}
              >
                Leave Team
              </WorkAgreementActionButton>
            )}
        </WorkAgreementActions>
        <MessageButtons>
          <ButtonContainer>
            <MessageButton
              size="medium"
              color="primary"
              onClick={handleOpenChatPanel}
            >
              <Badge
                variant="standard"
                color="secondary"
                overlap="rectangular"
              >
                <ChatIcon fontSize="medium" />
              </Badge>
            </MessageButton>
            <MessageButtonLabel
              variant="button"
              color={workAgreementId ? "primary" : "darkgray"}
            >
              Messages
            </MessageButtonLabel>
          </ButtonContainer>
        </MessageButtons>
      </ActionsContainer>
      <Portal>
        <ChatDrawer
          isOpen={isChatPanelOpen}
          isArchived={workAgreement?.isArchived}
          forums={forums}
          onOpen={handleOpenChatPanel}
          onClose={handleCloseChatPanel}
        />
      </Portal>
    </>
  )
}
